import React, { useState, useRef } from 'react';
import SelectFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldInline';
import { ContactSection, ContactHeaderSection, ContactCard, CardButton } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';

interface ContactEditorProps {
  data:     ContactSection;
  onChange: (v: ContactSection) => void;
}

const ContactEditor: React.FC<ContactEditorProps> = ({ data, onChange }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [cardEdits,   setCardEdits]   = useState<Record<number, Partial<ContactCard>>>({});

  const orig   = useRef(data);
  const header = data.content[0]?.headerSection;

  const cardOptions = orig.current.content[0]?.headerSection?.lists.map((c, i) => ({
    value: String(i),
    label: `Card${i + 1}/ ${c.title}`,
  })) ?? [];

  const setHeader = (p: Partial<ContactHeaderSection>) =>
    onChange({
      ...data,
      content: [{ ...data.content[0], headerSection: { ...header, ...p } }],
    });

  const setCardField = (field: keyof Omit<ContactCard, 'id'>, v: string) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), [field]: v },
    };
    setCardEdits(updatedEdits);
    if (!header) return;
    setHeader({ lists: header.lists.map((c, i) => ({ ...c, ...(updatedEdits[i] ?? {}) })) });
  };

  const setCardButton = (label: string) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), button: { label } },
    };
    setCardEdits(updatedEdits);
    if (!header) return;
    setHeader({ lists: header.lists.map((c, i) => ({ ...c, ...(updatedEdits[i] ?? {}) })) });
  };

  if (!header) return null;

  const origCard = orig.current.content[0]?.headerSection?.lists[selectedIdx];

  return (
    <>
      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current.content[0]?.headerSection?.title ?? ''}>
        <DraftTextField
          placeholder="Text title"
          onChange={v => setHeader({ title: v })}
        />
      </SplitRow>

      <LabelRow label="Main description" />
      <SplitRow currentText={orig.current.content[0]?.headerSection?.description ?? ''} multiline>
        <DraftTextArea
          placeholder="Main description"
          rows={3}
          onChange={v => setHeader({ description: v })}
        />
      </SplitRow>

      <div className="scl--cms-content-edit-contact-card-row">
        <span className="scl--cms-content-edit-contact-label">Card / Contact us</span>
        <div className="scl--cms-content-edit-contact-card-row-right">
          <SelectFieldInline
            className="scl--cms-content-edit-contact-card-select"
            value={String(selectedIdx)}
            options={cardOptions}
            onChange={(v: string) => setSelectedIdx(Number(v))}
          />
        </div>
      </div>
      <div className="scl--cms-content-edit-contact-divider" />

      {origCard && (
        <>
          <LabelRow label="Card title" />
          <SplitRow currentText={origCard.title}>
            <DraftTextField
              key={`title-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.title ?? ''}
              placeholder="Card title"
              onChange={v => setCardField('title', v)}
            />
          </SplitRow>

          <LabelRow label="Card description" />
          <SplitRow currentText={origCard.description} multiline>
            <DraftTextArea
              key={`desc-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.description ?? ''}
              placeholder="Card description"
              rows={3}
              onChange={v => setCardField('description', v)}
            />
          </SplitRow>

          <LabelRow label="Card button" />
          <SplitRow currentText={origCard.button.label}>
            <DraftTextField
              key={`btn-${selectedIdx}`}
              initialValue={(cardEdits[selectedIdx]?.button as CardButton | undefined)?.label ?? ''}
              placeholder="Text button"
              onChange={v => setCardButton(v)}
            />
          </SplitRow>
        </>
      )}
    </>
  );
};

export default ContactEditor;